import {useRef, useState} from 'react';
import type {ChangeEvent} from 'react';
import {
  AlertTriangle,
  Download,
  FileText,
  RotateCcw,
  Upload,
} from 'lucide-react';
import {Button} from '@/components/ui/Button';
import {Card, CardContent} from '@/components/ui/Card';
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/Tabs';
import {Textarea} from '@/components/ui/Textarea';
import {TooltipProvider} from '@/components/ui/Tooltip';
import {useEdiDocument} from './hooks/useEdiDocument';
import {downloadTextFile, readFileAsText} from './lib/file-io';
import {EnvelopeNavigator} from './EnvelopeNavigator';
import {DataSections} from './DataSections';
import {ValidationSummary} from './ValidationSummary';

/**
 * The EDI Viewer: load a raw EDI X12 document (by pasting text or uploading
 * a file), see it explained and validated, edit field values, and export the
 * result. Today 810, 840, 843, 844, 845, 850, 852, 855, 856, 867 and 997
 * transactions are explained and editable; other transaction sets are shown
 * read-only.
 * Everything happens in this component's own state and the browser — no
 * document content is ever sent to a server (see docs/edi-viewer.md).
 */
export function EdiViewerPage() {
  const {state, loadRaw, editElement, selectTransaction, reset, exportRawText} =
    useEdiDocument();
  const [pastedText, setPastedText] = useState('');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset the input so choosing the same file again still fires a change.
    event.target.value = '';
    if (!file) return;

    try {
      const text = await readFileAsText(file);
      setUploadError(null);
      loadRaw(text);
    } catch (err) {
      setUploadError(
        err instanceof Error ? err.message : 'Could not read that file.'
      );
    }
  };

  const showEntry = state.status !== 'loaded';
  const activeTransaction =
    state.envelope?.groups[state.activeGroupIndex]?.transactions[
      state.activeTransactionIndex
    ];
  const findings = state.findings ?? [];

  // Always available once a document is loaded — never disabled by a
  // validation finding, per the requirement that export is never blocked.
  const handleExport = () => {
    const text = exportRawText();
    if (!text) return;
    const identifier =
      activeTransaction?.parsed810?.header.big?.elements[1] ??
      activeTransaction?.parsed840?.header.bqt?.elements[1] ??
      activeTransaction?.parsed843?.header.bqr?.elements[1] ??
      activeTransaction?.parsed844?.header.baa?.elements[4] ??
      activeTransaction?.parsed845?.header.bpa?.elements[1] ??
      activeTransaction?.parsed850?.header.beg?.elements[2] ??
      activeTransaction?.parsed852?.header.bct?.elements[2] ??
      activeTransaction?.parsed855?.header.bak?.elements[2] ??
      activeTransaction?.parsed856?.header.bsn?.elements[1] ??
      activeTransaction?.parsed867?.header.bpt?.elements[1] ??
      activeTransaction?.parsed997?.ak1?.elements[1];
    const suffix =
      typeof identifier === 'string' && identifier ? `-${identifier}` : '';
    const prefix = activeTransaction?.transactionSetCode || 'edi';
    downloadTextFile(`${prefix}${suffix}.edi`, text);
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">EDI Viewer</h1>
        <p className="text-sm text-muted-foreground">
          View, explain and edit EDI X12 documents. Supported today: 810
          (Invoice), 840 (Request for Quotation), 843 (Response to Request for
          Quotation), 844 (Product Transfer Account Adjustment), 845 (Price
          Authorization Acknowledgment/Status), 850 (Purchase Order), 852
          (Product Activity/POS), 855 (Purchase Order Acknowledgment), 856 (Ship
          Notice/Manifest), 867 (Product Transfer and Resale Report), and 997
          (Functional Acknowledgment).
        </p>
      </div>

      {state.status === 'error' && (
        <Card className="border-destructive/50 bg-destructive/5">
          <CardContent className="flex items-start gap-3 py-4">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-destructive" />
            <div className="text-sm">
              <p className="font-medium text-destructive">
                Couldn&apos;t load that document
              </p>
              <p className="text-muted-foreground">{state.loadError}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {uploadError && (
        <Card className="border-destructive/50 bg-destructive/5">
          <CardContent className="py-3 text-sm text-destructive">
            {uploadError}
          </CardContent>
        </Card>
      )}

      {showEntry ? (
        <Card>
          <CardContent className="py-5">
            <Tabs defaultValue="paste">
              <TabsList>
                <TabsTrigger value="paste">Paste text</TabsTrigger>
                <TabsTrigger value="upload">Upload file</TabsTrigger>
              </TabsList>

              <TabsContent value="paste" className="space-y-2">
                <Textarea
                  value={pastedText}
                  onChange={e => setPastedText(e.target.value)}
                  placeholder="Paste raw EDI X12 text here, starting with ISA..."
                  className="min-h-[220px] font-mono text-xs"
                />
                <Button
                  onClick={() => loadRaw(pastedText)}
                  disabled={pastedText.trim() === ''}
                >
                  <FileText className="h-3.5 w-3.5" />
                  Load pasted document
                </Button>
              </TabsContent>

              <TabsContent value="upload" className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Choose a raw .edi/.txt file containing an X12 interchange.
                </p>
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="h-3.5 w-3.5" />
                  Choose file…
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".edi,.txt,.x12,text/plain"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            {state.envelope && (
              <EnvelopeNavigator
                envelope={state.envelope}
                activeGroupIndex={state.activeGroupIndex}
                activeTransactionIndex={state.activeTransactionIndex}
                onSelect={selectTransaction}
              />
            )}
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={reset}>
                <RotateCcw className="h-3.5 w-3.5" />
                Load a different document
              </Button>
              <Button size="sm" onClick={handleExport}>
                <Download className="h-3.5 w-3.5" />
                Export
              </Button>
            </div>
          </div>

          {activeTransaction?.parsed810 ||
          activeTransaction?.parsed840 ||
          activeTransaction?.parsed843 ||
          activeTransaction?.parsed844 ||
          activeTransaction?.parsed845 ||
          activeTransaction?.parsed850 ||
          activeTransaction?.parsed852 ||
          activeTransaction?.parsed855 ||
          activeTransaction?.parsed856 ||
          activeTransaction?.parsed867 ||
          activeTransaction?.parsed997 ? (
            <TooltipProvider delayDuration={300}>
              {/* Keyed by transaction so switching transactions starts with
                  every section collapsed again rather than inheriting open
                  panels. */}
              <div
                key={`${state.activeGroupIndex}-${state.activeTransactionIndex}`}
                className="space-y-2"
              >
                <ValidationSummary
                  findings={findings}
                  activeGroupIndex={state.activeGroupIndex}
                  activeTransactionIndex={state.activeTransactionIndex}
                />
                <DataSections
                  groupIndex={state.activeGroupIndex}
                  transactionIndex={state.activeTransactionIndex}
                  transaction={activeTransaction}
                  findings={findings}
                  onEdit={editElement}
                />
              </div>
            </TooltipProvider>
          ) : (
            activeTransaction && (
              <Card>
                <CardContent className="space-y-2 py-4">
                  <p className="text-sm font-medium">
                    Transaction set{' '}
                    {activeTransaction.transactionSetCode || '(unknown)'} is not
                    an 810, 840, 843, 844, 845, 850, 852, 855, 856, 867, or 997
                    — shown read-only.
                  </p>
                  <pre className="max-h-96 overflow-auto rounded-md bg-muted/40 p-3 text-xs">
                    {[
                      activeTransaction.stSegment,
                      ...activeTransaction.segments,
                    ]
                      .map(s => `${s.id}*${s.elements.map(String).join('*')}`)
                      .join('\n')}
                  </pre>
                </CardContent>
              </Card>
            )
          )}
        </div>
      )}
    </div>
  );
}
